import React, { useState, useEffect } from 'react';
import { useParams, Link, Outlet, useLocation } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Card } from '../../../components/ui/card';
import { CreditCard, TrendingUp, FileText, Wallet } from 'lucide-react';

export default function Billing() {
  const { orgId } = useParams();
  const location = useLocation();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
  }, [orgId]);

  const fetchOverview = async () => {
    try {
      const response = await apiClient.get(`/billing/orgs/${orgId}/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch billing overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSubPage = !location.pathname.endsWith('/billing');

  if (isSubPage) {
    return <Outlet />;
  }

  return (
    <div className="p-8 space-y-6" data-testid="billing-overview">
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-600 mt-1">Manage your subscription and billing</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-lg animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900">Current Plan</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900 capitalize">
                {overview?.currentPlan || 'Free'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Renews on {overview?.renewalDate ? new Date(overview.renewalDate).toLocaleDateString() : 'N/A'}
              </p>
            </Card>

            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-enterprise-slate/10 rounded-lg">
                  <Wallet className="w-5 h-5 text-enterprise-slate" />
                </div>
                <h3 className="font-semibold text-gray-900">Seats</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {overview?.seatsUsed || 0} / {overview?.seatsTotal || 10}
              </p>
              <p className="text-sm text-gray-600 mt-1">Members active</p>
            </Card>

            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-success/10 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-success" />
                </div>
                <h3 className="font-semibold text-gray-900">Billing Email</h3>
              </div>
              <p className="text-sm font-medium text-gray-900 truncate">
                {overview?.billingEmail || 'Not set'}
              </p>
              <p className="text-sm text-gray-600 mt-1">Payment notifications</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to={`/enterprise/orgs/${orgId}/billing/usage`}>
              <Card className="p-6 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Usage</h3>
                    <p className="text-sm text-gray-600">View detailed usage metrics</p>
                  </div>
                </div>
              </Card>
            </Link>

            <Link to={`/enterprise/orgs/${orgId}/billing/invoices`}>
              <Card className="p-6 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-enterprise-slate/10 rounded-lg">
                    <FileText className="w-6 h-6 text-enterprise-slate" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Invoices</h3>
                    <p className="text-sm text-gray-600">View and download invoices</p>
                  </div>
                </div>
              </Card>
            </Link>

            <Link to={`/enterprise/orgs/${orgId}/billing/payment-methods`}>
              <Card className="p-6 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-success/10 rounded-lg">
                    <CreditCard className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Payment Methods</h3>
                    <p className="text-sm text-gray-600">Manage cards and billing info</p>
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
